import { useNavigate } from "@remix-run/react";
import cx from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import lodash from "lodash";
import { ChevronRight } from "lucide-react";
import qs from "qs";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Popup } from "reactjs-popup";
import { setModalParams } from "~/containers/Modal";
import renderDigits from "~/helpers/renderDigits";

const renderQualityDots = ({ product, ranges }) => {
  const quality = product.inventory?.quality;

  if (lodash.isUndefined(quality))
    return (
      <div className="flex gap-0.5 me-1 cursor-pointer">
        <span className="empty-dot" />
      </div>
    );

  if (quality <= ranges[0]) return <span className="red-dot cursor-pointer" />;
  if (quality > ranges[0] && quality <= ranges[1])
    return (
      <div className="flex gap-0.5 me-1 cursor-pointer">
        <span className="yellow-dot" />
        <span className="yellow-dot" />
      </div>
    );

  if (quality > ranges[1])
    return (
      <div className="flex gap-0.5 me-1 cursor-pointer">
        <span className="green-dot" />
        <span className="green-dot" />
        <span className="green-dot" />
      </div>
    );

  return (
    <div className="flex gap-0.5 me-1 cursor-pointer">
      <span className="empty-dot" />
    </div>
  );
};

const calculateQualityGrade = ({ product, ranges }) => {
  const quality = product.inventory?.quality;

  if (lodash.isUndefined(quality)) return { color: "empty", grade: "unknown" };
  if (quality <= ranges[0]) return { color: "red", grade: "poor" };
  if (quality > ranges[0] && quality <= ranges[1])
    return { color: "yellow", grade: "moderate" };
  if (quality > ranges[1]) return { color: "green", grade: "standard" };
  return { color: "empty", grade: "unknown" };
};

const renderQuantity = ({ product, i18n, t }) => {
  return (
    <div className="cursor-pointer">
      {product.inventory?.quantity > 0 && (
        <>
          {renderDigits({
            data: product.inventory?.quantity,
            i18n,
          })}{" "}
          <span className="lowercase">{t("numbersAvailable")}</span>
        </>
      )}
      {!product.inventory?.quantity && (
        <span className="lowercase">{t("noNumbersAvailable")}</span>
      )}
    </div>
  );
};

interface ProductProps {
  product: any;
  productsToShow: any[];
  index: number;
  showAll: boolean;
  setShowAll: React.Dispatch<React.SetStateAction<boolean>>;
  ranges: number[];
  i18n: any;
  t: any;
  user: any; // Add the 'user' property to the interface
  cookie: any; // Add the 'cookie' property to the interface
  params: any; // Add the 'cookie' property to the interface
}

const Product: React.FC<ProductProps> = React.memo(
  ({
    product,
    productsToShow,
    index,
    cookie,
    params,
    user,
    showAll,
    setShowAll,
    ranges,
  }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    const onProductClick = ({ product }) => {
      if (!showAll) return setShowAll(true);
      if (cookie) {
        if (calculateQualityGrade({ product, ranges }).grade !== "standard") {
          return dispatch(
            setModalParams({
              target: "alertForLowQualityProduct",
              props: {
                product,
                params,
                cookie,
                user,
                onNextModalClick: () =>
                  dispatch(
                    setModalParams({
                      target: "orderConfirmation",
                      props: { product, params, cookie, user },
                    }),
                  ),
              },
            }),
          );
        }
        dispatch(
          setModalParams({
            target: "orderConfirmation",
            props: { product, params, cookie, user },
          }),
        );
      } else {
        navigate(
          "/login?" + qs.stringify({ next: "/services/" + params?.sid }),
        );
      }
    };

    const borderRadius = useMemo(() => {
      if (!showAll) {
        if (index === 0) return "1rem 1rem 0 0";
        if (index === productsToShow.length - 1 && !showAll) return "1rem";
        return "1rem";
      } else return "0";
    }, [index, showAll]);

    return (
      <motion.div
        key={product._id}
        onClick={() => {
          if (!showAll) return setShowAll(true);
        }}
        className="relative overflow-hidden"
        initial={{ opacity: 0, y: 50 }}
        animate={{
          opacity: 1,
          y: 0,
          marginTop: !showAll && index > 0 ? "-1.2rem" : "0",
          zIndex: !showAll ? index : 1,
          borderRadius,
        }}
        transition={{
          duration: 0.3,
          delay: Math.min(index * 0.1, 10 * 0.1),
        }}
        style={{
          overflow: "hidden",
          marginTop: !showAll && index > 0 ? "-1.2rem" : "0",
          zIndex: !showAll ? index : 1,
          borderRadius,
        }}
      >
        <AnimatePresence>
          {!showAll && (
            <motion.div
              className="absolute top-0 left-0 w-full h-full bg-gray-500/60"
              initial={{ opacity: 0, scale: 1.1 }}
              animate={{
                opacity: !showAll ? 0.1 + index / 20 : 1,
                scale: 1,
              }}
              exit={{
                opacity: 0,
                scale: 1.1,
                transition: { duration: 0.2 },
              }}
              transition={{
                duration: 0.4,
                ease: "easeInOut",
                delay: index * 0.05,
              }}
            />
          )}
        </AnimatePresence>
        <div className="flex justify-start bg-base-100 ps-4 gap-3 w-full items-center disabled:opacity-50 disabled:cursor-not-allowed">
          <div className="flex justify-start items-center">
            <div
              className={`h-8 w-8 rounded-full image-full bg-center bg-cover bg-no-repeat border fi-${product._country?.name?.a2?.toLowerCase()}`}
            />
          </div>

          <div
            className={cx("flex justify-between items-center grow pe-6", {
              "border-b border-slate-200/50": showAll,
            })}
          >
            <div className="flex flex-col gap-1 py-2 items-start relative">
              <h5 className="font-semibold capitalize block whitespace-nowrap overflow-ellipsis overflow-x-hidden max-w-48 md:max-w-52 xl:max-w-56">
                {product._country?.name.common}
              </h5>
              <div className="text-neutral-500 text-xs font-medium font-['Figtree', 'IRANYekanXVF'] leading-none flex gap-1 items-center">
                <AnimatePresence>
                  <Popup
                    trigger={() => renderQualityDots({ product, ranges })}
                    position={
                      i18n.dir() === "ltr" ? "bottom left" : "bottom right"
                    }
                    on="hover"
                    arrow={false}
                    contentStyle={{
                      padding: 0,
                      border: 0,
                      backgroundColor: "transparent",
                      boxShadow: "none",
                    }}
                  >
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                      className="rounded-box z-[1] mt-4 w-52 border border-neutral-500 border-opacity-20 bg-base-100 px-4 py-3 shadow-2xl flex flex-col gap-3"
                    >
                      <motion.p
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1, duration: 0.2 }}
                        className="text-xs font-['Figtree', 'IRANYekanXVF']"
                      >
                        {t("qualityTheBetterTheHigher")}
                      </motion.p>
                      <motion.div
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2, duration: 0.2 }}
                        className="flex gap-2 me-1 items-center text-sm"
                      >
                        <span className="font-bold">
                          {t(
                            `grades.${
                              calculateQualityGrade({ product, ranges }).grade
                            }`,
                          )}
                          :
                        </span>
                        {renderQualityDots({ product, ranges })}
                      </motion.div>
                    </motion.div>
                  </Popup>
                </AnimatePresence>

                <AnimatePresence>
                  <Popup
                    trigger={() => renderQuantity({ product, i18n, t })}
                    position={
                      i18n.dir() === "ltr" ? "bottom left" : "bottom right"
                    }
                    on="hover"
                    arrow={false}
                    contentStyle={{
                      padding: 0,
                      border: 0,
                      backgroundColor: "transparent",
                      boxShadow: "none",
                    }}
                  >
                    <div
                      className={cx(
                        "bg-gray-400 w-5 h-5 rounded-full absolute top-1 z-10",
                        {
                          "right-4": i18n.dir() === "rtl",
                          "left-4": i18n.dir() === "ltr",
                        },
                      )}
                    />
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                      className="rounded-xl z-10 relative mt-3 w-60 bg-gray-400 px-4 py-3 shadow-2xl flex flex-col gap-3"
                    >
                      <motion.p
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1, duration: 0.2 }}
                        className="text-xs font-['Figtree', 'IRANYekanXVF'] text-white"
                      >
                        {t("theCountOfAvailableNumbersOperatorIndicated")}
                      </motion.p>
                    </motion.div>
                  </Popup>
                </AnimatePresence>
              </div>
            </div>
            <button
              className="flex gap-1 items-center"
              onClick={() => onProductClick({ product })}
            >
              <span
                className={cx(
                  "items-center justify-center text-xs font-mono leading-none flex shrink rounded-full px-2.5 py-1.5",
                  {
                    "bg-emerald-100 text-emerald-700":
                      product.inventory?.quantity,
                    "bg-red-100 text-red-700": !product.inventory?.quantity,
                  },
                )}
              >
                {product.price}
              </span>
              <span className="flex justify-center items-center">
                <ChevronRight
                  size={18}
                  className={cx({
                    "rotate-180": i18n.dir() === "rtl",
                  })}
                />
              </span>
            </button>
          </div>
        </div>
      </motion.div>
    );
  },
);

export default Product;
